import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, 
  FileText, 
  BookOpen, 
  Headphones, 
  Share2,
  Heart,
  Home,
  ExternalLink,
  PenLine,
  GraduationCap,
  Bookmark,
  Sparkles,
  Gift
} from 'lucide-react';
import { DownloadPDF } from '../components/DownloadPDF';
import { bookContent } from '../data/bookContent';
import { freeResources } from '../data/freeResources';
import { workbookContent, worksheetContent, prayerGuideContent, transformationJournalContent } from '../data/bonusContent';
import { toast } from 'sonner';

const PRINT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Nunito:wght@400;600&display=swap');
  @page { size: A4; margin: 2cm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Nunito', sans-serif; color: #2D2030; line-height: 1.8; font-size: 11pt; }
  h1, h2, h3 { font-family: 'Cormorant Garamond', serif; }
  .cover { text-align: center; padding: 80px 20px; page-break-after: always; }
  .cover h1 { font-size: 34pt; color: #2D2030; }
  .cover .subtitle { font-size: 14pt; color: #7B1045; font-style: italic; margin: 10px 0 30px; }
  .divider { width: 60px; height: 2px; background: #D4AF37; margin: 20px auto; }
  .section { page-break-inside: avoid; margin-bottom: 35px; }
  .section-header { background: linear-gradient(135deg, #7B1045 0%, #A8366B 100%); color: white; padding: 20px 25px; border-radius: 8px 8px 0 0; }
  .section-header h2 { font-size: 18pt; margin-bottom: 3px; }
  .section-header p { font-size: 11pt; opacity: 0.9; }
  .scripture-box { background: #FFF9E6; padding: 18px; border-left: 4px solid #D4AF37; margin: 15px 0; }
  .scripture-box p { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 13pt; }
  .scripture-box span { display: block; color: #D4AF37; font-size: 10pt; margin-top: 6px; font-style: normal; }
  .prayer-box { background: #FDF5F8; padding: 22px; border-radius: 8px; margin: 15px 0; }
  .prayer-box h3 { font-size: 13pt; color: #7B1045; margin-bottom: 8px; }
  .affirmation-box { background: #FFF9E6; padding: 18px; text-align: center; border-radius: 8px; margin: 15px 0; font-style: italic; font-family: 'Cormorant Garamond', serif; font-size: 13pt; }
  .line { border-bottom: 1px solid #E5E5E5; height: 32px; }
  .prompt { font-weight: 600; color: #2D2030; margin: 18px 0 8px 0; }
  .write-area { border: 1px dashed #D4D4D4; border-radius: 8px; padding: 12px; min-height: 100px; margin: 8px 0; background: #FEFCFD; }
  .gold-header { background: linear-gradient(135deg, #D4AF37 0%, #E8C547 100%); color: white; padding: 18px 22px; border-radius: 8px 8px 0 0; }
  .gold-header h3 { font-size: 10pt; opacity: 0.8; }
  .gold-header h2 { font-size: 16pt; }
  .prayer-body { padding: 22px; background: #FEFCFD; border: 1px solid #E5E5E5; border-top: none; border-radius: 0 0 8px 8px; }
  .prayer-body p { margin-bottom: 10px; }
  .checkbox-item { display: flex; align-items: flex-start; gap: 8px; margin: 8px 0; }
  .checkbox { width: 16px; height: 16px; border: 2px solid #7B1045; border-radius: 3px; flex-shrink: 0; margin-top: 3px; }
  .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E5E5; color: #6B5B6E; font-size: 9pt; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const FOOTER_HTML = `
  <div class="footer">
    <p><strong>By Janelle McGowan-Charles</strong></p>
    <p style="color: #7B1045;">Purpose Reclaimed Coaching</p>
    <p style="margin-top: 10px;">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
  </div>
`;

const printDocument = (content, title) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { printWindow.print(); }, 1000);
  toast.success(`${title} ready!`, { description: 'Choose "Save as PDF" in the print dialog' });
};

export const Downloads = () => {

  const generateKindleReady = () => {
    const kindleContent = `<!DOCTYPE html><html><head><title>${bookContent.title}</title><meta charset="UTF-8">
      <style>body{font-family:Georgia,serif;line-height:1.6;padding:20px;max-width:600px;margin:0 auto}h1{font-size:2em;text-align:center}h2{font-size:1.5em;margin-top:2em;border-bottom:1px solid #ccc;padding-bottom:.3em}.scripture{font-style:italic;background:#f9f9f9;padding:15px;margin:20px 0;border-left:3px solid #d4af37}.scripture .ref{display:block;text-align:right;font-size:.9em;color:#d4af37;margin-top:10px}.affirmation{background:#fff9e6;padding:15px;margin:20px 0;text-align:center;font-style:italic}.prayer{background:#f0f5f4;padding:20px;margin:20px 0}.journal{margin:20px 0;padding:15px;border:1px dashed #ccc}.chapter-break{page-break-before:always}.center{text-align:center}</style></head><body>
      <div class="center"><h1>${bookContent.title}</h1><p><em>${bookContent.subtitle}</em></p><p><strong>By Janelle McGowan-Charles</strong></p><p style="color:#7c9a92">Purpose Reclaimed Coaching</p><p style="font-size:.8em;color:#666;margin-top:20px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p></div>
      <hr style="margin:40px 0"><h2>A Letter to You</h2>${bookContent.introduction.split('\n\n').map(p => `<p>${p}</p>`).join('')}
      ${bookContent.chapters.map(ch => `<div class="chapter-break"><h2>Chapter ${ch.id}: ${ch.title}</h2><p><em>${ch.subtitle}</em></p><div class="scripture">"${ch.scripture.text}"<span class="ref">&mdash; ${ch.scripture.reference}</span></div>${ch.content.split('\n\n').map(p => `<p>${p}</p>`).join('')}<blockquote style="border-left:2px solid #7c9a92;padding-left:15px;margin:20px 0;font-style:italic;color:#666">"${ch.quote}"</blockquote><div class="affirmation"><strong>AFFIRMATION</strong><br>"${ch.affirmation}"</div><div class="journal"><h3>Reflection Questions</h3>${ch.journalQuestions.map((q,i) => `<p><strong>${i+1}.</strong> ${q}</p>`).join('')}</div><div class="prayer"><h3>Closing Prayer</h3>${ch.prayer.split('\n\n').map(p => `<p>${p}</p>`).join('')}</div></div>`).join('')}
      </body></html>`;
    const blob = new Blob([kindleContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'breakthrough-kindle-ready.html';
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Kindle-ready HTML downloaded!');
  };

  const generatePrayerWorkbook = () => {
    const wb = workbookContent;
    const content = `<!DOCTYPE html><html><head><title>${wb.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}</style></head><body>
      <div class="cover">
        <h1>${wb.title.replace('&', '&amp;')}</h1>
        <p class="subtitle">${wb.subtitle}</p>
        <div class="divider"></div>
        <p style="color:#D4AF37;font-size:12pt;margin-top:20px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt;margin-top:5px">Purpose Reclaimed Coaching</p>
        <p style="font-size:9pt;color:#6B5B6E;margin-top:30px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p>
      </div>
      <div style="padding:20px;margin-bottom:40px;page-break-after:always">
        <h2 style="font-size:22pt;color:#2D2030;margin-bottom:15px">Welcome, Sister</h2>
        <p>This workbook was created specifically for women who have experienced sexual abuse. It is your sacred space to pray, reflect, and journal your way toward healing.</p>
        <p style="margin-top:15px">The questions in this workbook are different from those in the Breakthrough ebook. They go deeper into the specific wounds of abuse — your body, your trust, your boundaries, your identity. There are no right or wrong answers. Simply be honest with God and yourself.</p>
        <p style="margin-top:15px;font-style:italic;color:#7B1045">"Pour out your hearts to Him, for God is our refuge." &mdash; Psalm 62:8</p>
      </div>
      ${wb.sections.map(s => `
        <div class="section" style="page-break-before:always">
          <div class="section-header"><h2>${s.title}</h2><p>${s.subtitle}</p></div>
          <div class="scripture-box"><p>"${s.scripture.text}"</p><span>&mdash; ${s.scripture.ref}</span></div>
          <div class="prayer-box"><h3>Prayer</h3><p>${s.prayer}</p></div>
          <div class="affirmation-box"><strong style="display:block;font-size:10pt;letter-spacing:2px;margin-bottom:8px;font-style:normal">DAILY AFFIRMATION</strong>"${s.affirmation}"</div>
          ${s.questions.map((q,i) => `<div style="margin:25px 0"><p class="prompt">${i+1}. ${q}</p><div style="margin:10px 0">${Array(8).fill('<div class="line"></div>').join('')}</div></div>`).join('')}
        </div>
      `).join('')}
      <div style="page-break-before:always;text-align:center;padding:40px 20px">
        <h2 style="font-size:22pt;margin-bottom:15px">Keep Going, Sister</h2>
        <p style="max-width:400px;margin:0 auto">Your healing is not a straight line. Some days will be harder than others. But every word you wrote in this workbook is a step toward freedom. God sees your courage. He is proud of you.</p>
        <p style="margin-top:20px;font-style:italic;color:#7B1045">"She is clothed with strength and dignity; she can laugh at the days to come." &mdash; Proverbs 31:25</p>
      </div>
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, 'Prayer & Journaling Workbook');
  };

  const generateWorksheets = () => {
    const ws = worksheetContent;
    const content = `<!DOCTYPE html><html><head><title>${ws.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}</style></head><body>
      <div class="cover">
        <h1>${ws.title}</h1>
        <p class="subtitle">${ws.subtitle}</p>
        <div class="divider"></div>
        <p style="color:#D4AF37;font-size:12pt;margin-top:20px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt;margin-top:5px">Purpose Reclaimed Coaching</p>
        <p style="font-size:9pt;color:#6B5B6E;margin-top:30px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p>
      </div>
      ${ws.sheets.map(sheet => `
        <div style="page-break-before:always;padding:20px 0">
          <div class="section-header"><h2>${sheet.title}</h2></div>
          <div style="padding:20px">
            <p style="margin-bottom:20px">${sheet.intro}</p>
            ${sheet.exercises.map(ex => `
              <div style="margin-bottom:25px">
                <h3 style="font-size:13pt;color:#7B1045;margin-bottom:8px;border-bottom:1px solid #E5E5E5;padding-bottom:5px">${ex.heading}</h3>
                <p style="margin-bottom:8px">${ex.prompt}</p>
                ${ex.lines ? `<div class="write-area">${Array(ex.lines).fill('<div class="line"></div>').join('')}</div>` : ''}
              </div>
            `).join('')}
            <div class="scripture-box"><p>${sheet.scripture}</p></div>
          </div>
        </div>
      `).join('')}
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, 'Healing Worksheets');
  };

  const generateBonusPrayerGuide = () => {
    const pg = prayerGuideContent;
    const content = `<!DOCTYPE html><html><head><title>${pg.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}</style></head><body>
      <div class="cover">
        <p style="font-size:10pt;letter-spacing:3px;color:#D4AF37;text-transform:uppercase">Bonus Guide</p>
        <h1>${pg.title}</h1>
        <p class="subtitle">${pg.subtitle}</p>
        <div class="divider"></div>
        <p style="color:#2D2030;font-size:12pt;margin-top:20px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt">Purpose Reclaimed Coaching</p>
        <p style="font-size:9pt;color:#6B5B6E;margin-top:30px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p>
      </div>
      <div style="padding:20px;margin-bottom:30px;page-break-after:always">
        <h2 style="font-size:22pt;color:#2D2030;margin-bottom:15px">Before You Begin</h2>
        <p>These 30 prayers were written specifically for women healing from sexual abuse. They are raw, honest, and deeply personal — because that is what healing requires.</p>
        <p style="margin-top:12px">You don't need to pray them perfectly. You don't need to feel ready. Just open your heart and let God meet you where you are. He can handle your anger, your tears, your doubts, and your questions.</p>
        <p style="margin-top:12px;font-style:italic;color:#7B1045">"The Spirit helps us in our weakness. We do not know what we ought to pray for, but the Spirit himself intercedes for us through wordless groans." &mdash; Romans 8:26</p>
      </div>
      ${pg.sections.map(section => `
        <div style="page-break-before:always">
          <h2 style="font-size:22pt;color:#D4AF37;margin-bottom:5px">${section.week}</h2>
          <p style="font-size:16pt;color:#2D2030;margin-bottom:25px;font-family:'Cormorant Garamond',serif">${section.theme}</p>
          ${section.prayers.map(p => `
            <div class="section">
              <div class="gold-header"><h3>DAY ${p.day}</h3><h2>${p.title}</h2></div>
              <div class="prayer-body"><p>${p.text}</p><div class="scripture-box"><p>${p.scripture}</p></div></div>
            </div>
          `).join('')}
        </div>
      `).join('')}
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, '30-Day Prayer Guide');
  };

  const generateTransformationJournal = () => {
    const tj = transformationJournalContent;
    const content = `<!DOCTYPE html><html><head><title>${tj.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}
      .week-header { background: linear-gradient(135deg, #8B5E3C 0%, #A0522D 100%); color: white; padding: 20px 25px; border-radius: 8px 8px 0 0; }
      .week-body { padding: 25px; border: 1px solid #E5E5E5; border-top: none; border-radius: 0 0 8px 8px; }
    </style></head><body>
      <div class="cover">
        <p style="font-size:10pt;letter-spacing:3px;color:#D4AF37;text-transform:uppercase">Full Purpose Package Exclusive</p>
        <h1>${tj.title}</h1>
        <p class="subtitle">${tj.subtitle}</p>
        <div class="divider"></div>
        <p style="color:#2D2030;font-size:12pt;margin-top:20px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt">Purpose Reclaimed Coaching</p>
        <p style="font-size:9pt;color:#6B5B6E;margin-top:30px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p>
      </div>
      <div style="padding:20px;margin-bottom:30px;page-break-after:always">
        <h2 style="font-size:22pt;color:#2D2030;margin-bottom:15px">Your 12-Week Journey</h2>
        <p>This is not a workbook you rush through. It is a 12-week guided journal designed to walk you through the deepest layers of healing from sexual abuse.</p>
        <p style="margin-top:12px">Each week focuses on one specific area of recovery. The questions are raw and real — because surface-level journaling won't produce deep-level healing.</p>
        <p style="margin-top:12px">Commit to one week at a time. There's no deadline. Take breaks when you need to. Return when you're ready. This journal will wait for you.</p>
        <p style="margin-top:12px;font-style:italic;color:#7B1045">"He who began a good work in you will carry it on to completion." &mdash; Philippians 1:6</p>
      </div>
      ${tj.weeks.map(w => `
        <div style="page-break-before:always">
          <div class="week-header">
            <h3 style="font-size:11pt;opacity:0.8">WEEK ${w.week}</h3>
            <h2 style="font-size:20pt">${w.title}</h2>
          </div>
          <div class="week-body">
            <p style="margin-bottom:15px">${w.prompt}</p>
            <div class="scripture-box"><p>"${w.scripture}"</p></div>
            ${w.questions.map((q,i) => `
              <div style="margin:25px 0">
                <p class="prompt">${i+1}. ${q}</p>
                <div class="write-area">${Array(10).fill('<div class="line"></div>').join('')}</div>
              </div>
            `).join('')}
          </div>
        </div>
      `).join('')}
      <div style="page-break-before:always;text-align:center;padding:60px 20px">
        <h2 style="font-size:26pt;margin-bottom:20px">You Made It, Sister</h2>
        <p style="max-width:450px;margin:0 auto;font-size:12pt">12 weeks ago you began this journey with courage. Today you stand as living proof that healing is possible. Your story is not over — it's just beginning. The woman you're becoming will change the world.</p>
        <p style="margin-top:25px;font-style:italic;color:#D4AF37;font-size:14pt;font-family:'Cormorant Garamond',serif">"She is clothed with strength and dignity; she can laugh at the days to come." &mdash; Proverbs 31:25</p>
      </div>
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, 'Reclaiming Your Story Journal');
  };

  const generateChapter1Preview = () => {
    const chapter = bookContent.chapters[0];
    const content = `<!DOCTYPE html><html><head><title>Chapter 1 Preview — ${bookContent.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}
      .preview-badge { display: inline-block; background: #D4AF37; color: white; padding: 6px 16px; border-radius: 20px; font-size: 10pt; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 15px; }
    </style></head><body>
      <div class="cover">
        <span class="preview-badge">Free Preview</span>
        <h1>${bookContent.title}</h1>
        <p class="subtitle">${bookContent.subtitle}</p>
        <div class="divider"></div>
        <p style="font-size:16pt;color:#2D2030;margin-top:20px;font-family:'Cormorant Garamond',serif">Chapter 1: ${chapter.title}</p>
        <p style="font-size:12pt;color:#6B5B6E;font-style:italic;margin-top:5px">${chapter.subtitle}</p>
        <p style="color:#D4AF37;font-size:12pt;margin-top:30px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt;margin-top:5px">Purpose Reclaimed Coaching</p>
        <p style="font-size:9pt;color:#6B5B6E;margin-top:30px">&copy; ${new Date().getFullYear()} Janelle McGowan-Charles. All rights reserved.</p>
      </div>
      <div style="padding:20px;margin-bottom:30px;page-break-after:always">
        <h2 style="font-size:22pt;color:#2D2030;margin-bottom:15px">A Letter to You</h2>
        ${bookContent.introduction.split('\n\n').map(p => `<p style="margin-bottom:12px">${p}</p>`).join('')}
      </div>
      <div style="page-break-before:always">
        <div class="section-header"><h2>Chapter 1: ${chapter.title}</h2><p>${chapter.subtitle}</p></div>
        <div class="scripture-box"><p>"${chapter.scripture.text}"</p><span>&mdash; ${chapter.scripture.reference}</span></div>
        ${chapter.content.split('\n\n').map(p => `<p style="margin-bottom:15px;line-height:1.8">${p}</p>`).join('')}
        <div class="affirmation-box"><strong style="display:block;font-size:10pt;letter-spacing:2px;margin-bottom:8px;font-style:normal">AFFIRMATION</strong>"${chapter.affirmation}"</div>
        <blockquote style="border-left:3px solid #7B1045;padding:15px 20px;margin:25px 0;font-style:italic;color:#4A5568;font-family:'Cormorant Garamond',serif;font-size:13pt">"${chapter.quote}"</blockquote>
        <div style="margin:30px 0">
          <h3 style="font-size:16pt;color:#7B1045;margin-bottom:15px">Reflection Questions</h3>
          <p style="margin-bottom:15px;color:#6B5B6E">Take time to honestly reflect on these questions.</p>
          ${chapter.journalQuestions.map((q, i) => `<div style="margin:25px 0"><p class="prompt">${i + 1}. ${q}</p><div class="write-area">${Array(8).fill('<div class="line"></div>').join('')}</div></div>`).join('')}
        </div>
        <div class="prayer-box"><h3>Closing Prayer</h3>${chapter.prayer.split('\n\n').map(p => `<p style="margin-bottom:10px">${p}</p>`).join('')}</div>
      </div>
      <div style="page-break-before:always;text-align:center;padding:60px 20px">
        <h2 style="font-size:22pt;margin-bottom:15px">Continue Your Breakthrough Journey</h2>
        <p style="max-width:450px;margin:0 auto">This is just the beginning. The full Breakthrough guide includes 5 more transformative chapters covering shame, guilt, victim mentality, forgiveness, and discovering your true potential.</p>
        <p style="margin-top:20px;font-style:italic;color:#7B1045">Visit purposereclaim.com to get the full guide</p>
      </div>
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, 'Chapter 1 Preview');
  };

  const generate5LiesPDF = () => {
    const { fiveLies } = freeResources;
    const content = `<!DOCTYPE html><html><head><title>${fiveLies.title}</title><meta charset="UTF-8"><style>${PRINT_STYLES}
      .lie-header { background: linear-gradient(135deg, #E57373 0%, #EF9A9A 100%); color: white; padding: 15px 20px; border-radius: 8px 8px 0 0; }
      .truth-section { background: linear-gradient(135deg, #7B1045 0%, #A8366B 100%); color: white; padding: 15px 20px; }
      .reflection { padding: 18px; background: #FEFCFD; border-radius: 0 0 8px 8px; border: 1px solid #E5E5E5; border-top: none; }
    </style></head><body>
      <div class="cover">
        <h1>${fiveLies.title}</h1>
        <p class="subtitle">${fiveLies.subtitle}</p>
        <div class="divider"></div>
        <p style="color:#D4AF37;font-size:12pt;margin-top:20px">By Janelle McGowan-Charles</p>
        <p style="color:#7B1045;font-size:11pt">Purpose Reclaimed Coaching</p>
      </div>
      <div style="padding:20px;margin-bottom:30px">${fiveLies.introduction.split('\n\n').map(p => `<p style="margin-bottom:12px">${p}</p>`).join('')}</div>
      ${fiveLies.lies.map((item, idx) => `
        <div class="section">
          <div class="lie-header"><h3 style="font-size:10pt;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">Lie #${idx + 1}</h3><p style="font-size:16pt;font-style:italic">"${item.lie}"</p></div>
          <div class="truth-section"><h3 style="font-size:10pt;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">The Truth</h3><p style="font-size:13pt">${item.truth}</p></div>
          <div class="scripture-box"><p>"${item.scripture}"</p><span>&mdash; ${item.reference}</span></div>
          <div class="reflection"><p>${item.reflection}</p></div>
        </div>
      `).join('')}
      <div style="text-align:center;margin-top:40px;padding:25px;background:#FBF5F7;border-radius:8px">${fiveLies.closing.split('\n\n').map(p => `<p style="margin-bottom:10px">${p}</p>`).join('')}</div>
      ${FOOTER_HTML}</body></html>`;
    printDocument(content, '5 Lies Guide');
  };

  return (
    <div data-testid="downloads-page" className="min-h-screen bg-cream py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors">
            <Home className="w-5 h-5" /><span>Home</span>
          </Link>
          <Link to="/contents" className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors">
            <BookOpen className="w-5 h-5" /><span>Read Online</span>
          </Link>
        </div>

        <div className="text-center mb-12">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/98f4c0a7-37e3-4eee-b45d-3c901b2920ec/images/0470b11727d32ba005664c3bc8b17f0ec5bd3d699a6a10c5afd06c63414fb5d3.png"
            alt="Breakthrough Ebook Cover"
            className="w-32 mx-auto mb-4 rounded-lg shadow-xl"
          />
          <h1 className="font-heading text-4xl text-charcoal mb-2">Your Downloads</h1>
          <p className="text-charcoal-light">Access all your Breakthrough content</p>
        </div>

        <div className="space-y-6">

          {/* === FREE RESOURCES === */}
          <p className="text-xs font-bold text-gold uppercase tracking-wider mb-4">Free Resources</p>

          <div className="bg-gradient-to-r from-gold-light/20 to-cream rounded-2xl p-6 border border-gold/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Gift className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-gold uppercase tracking-wider">FREE</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">Chapter 1 Preview: The Fog of Trauma</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  The complete first chapter with scripture, reflection content, journal prompts, prayer, and affirmation.
                </p>
                <button onClick={generateChapter1Preview} data-testid="download-chapter1-preview-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download Chapter 1 Preview
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gold-light/20 to-cream rounded-2xl p-6 border border-gold/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-gold uppercase tracking-wider">FREE</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">5 Lies Survivors Believe</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  A mini-guide exposing 5 common lies from trauma, with God's truth, scripture, and reflection for each.
                </p>
                <button onClick={generate5LiesPDF} data-testid="download-5lies-free-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download 5 Lies Guide
                </button>
              </div>
            </div>
          </div>

          {/* === EBOOK PACKAGE === */}
          <p className="text-xs font-bold text-sage uppercase tracking-wider mb-4">Ebook Package</p>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-sage/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-sage" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-charcoal mb-1">Complete PDF E-Book</h3>
                <p className="text-sm text-charcoal-light mb-4">Beautifully formatted PDF with all 6 chapters, journal prompts, prayers, and affirmations.</p>
                <DownloadPDF />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-sage/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center flex-shrink-0">
                <PenLine className="w-7 h-7 text-sage" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-charcoal mb-1">Purpose Prayer & Journaling Workbook</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  All-new prayers, affirmations, and deep journaling prompts specifically for survivors — covering silence-breaking, body healing, trust rebuilding, boundaries, and stepping into purpose.
                </p>
                <button onClick={generatePrayerWorkbook} data-testid="download-workbook-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all">
                  <Download className="w-5 h-5" /> Download Workbook
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-charcoal mb-1">Kindle/E-Reader Version</h3>
                <p className="text-sm text-charcoal-light mb-4">Download for reading on your Kindle or e-reader device.</p>
                <button onClick={generateKindleReady} data-testid="download-kindle-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download Kindle Version
                </button>
              </div>
            </div>
          </div>

          {/* === TRANSFORMATION BUNDLE === */}
          <div className="mt-10">
            <p className="text-xs font-bold text-gold uppercase tracking-wider mb-4">Transformation Bundle</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gold/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-charcoal mb-1">Healing Worksheets</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  4 brand-new printable worksheets: Trigger Mapping, Self-Blame Release, Rebuilding Safety, and Reclaiming Healthy Relationships.
                </p>
                <button onClick={generateWorksheets} data-testid="download-worksheets-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download Worksheets
                </button>
              </div>
            </div>
          </div>

          <div id="bonus-section" className="bg-gradient-to-r from-gold-light/30 to-gold-light/10 rounded-2xl p-6 border border-gold/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/20 flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-gold uppercase tracking-wider">BONUS</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">5 Lies Survivors Believe</h3>
                <p className="text-sm text-charcoal-light mb-4">Bonus mini-guide with truth declarations and scripture for each lie.</p>
                <button onClick={generate5LiesPDF} data-testid="download-5lies-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download Bonus Guide
                </button>
              </div>
            </div>
          </div>

          {/* === FULL PURPOSE PACKAGE === */}
          <div className="mt-10">
            <p className="text-xs font-bold text-charcoal uppercase tracking-wider mb-4">Full Purpose Package</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-sage/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-7 h-7 text-sage" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-sage uppercase tracking-wider">PREMIUM</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">Purpose Reclaimed Workshop</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  4 self-paced sessions: Trauma Response, Releasing Shame, Forgiveness & Freedom, and Stepping Into Purpose.
                </p>
                <Link to="/workshop" data-testid="access-workshop-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all">
                  <ExternalLink className="w-5 h-5" /> Access Workshop
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-sage/5 to-gold-light/10 rounded-2xl p-6 border border-sage/20">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Bookmark className="w-7 h-7 text-gold" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-gold uppercase tracking-wider">BONUS</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">30-Day Prayer Guide</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  30 all-new prayers written specifically for abuse survivors — covering breaking silence, healing wounds, releasing shame, forgiveness, rebuilding, and walking in purpose.
                </p>
                <button onClick={generateBonusPrayerGuide} data-testid="download-prayer-guide-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all">
                  <Download className="w-5 h-5" /> Download Prayer Guide
                </button>
              </div>
            </div>
          </div>

          {/* NEW: Reclaiming Your Story - Transformation Journal */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-7 h-7 text-amber-700" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">EXCLUSIVE BONUS</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">Reclaiming Your Story — 12-Week Transformation Journal</h3>
                <p className="text-sm text-charcoal-light mb-4">
                  A deep-dive guided journal exclusively for Full Package members. 12 weeks covering: acknowledging impact, inner child healing, trauma responses, confronting shame, processing grief, holy anger, boundaries, forgiveness, intimacy after abuse, breaking generational cycles, finding purpose, and your overcomer declaration.
                </p>
                <button onClick={generateTransformationJournal} data-testid="download-transformation-journal-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white rounded-full font-medium hover:bg-amber-800 transition-all">
                  <Download className="w-5 h-5" /> Download Transformation Journal
                </button>
              </div>
            </div>
          </div>

          {/* Audio & Quotes */}
          <div className="bg-gradient-to-r from-sky-light/30 to-sage-light/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                <Share2 className="w-7 h-7 text-sage" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold text-sage uppercase tracking-wider">INCLUDED</span>
                <h3 className="font-heading text-xl text-charcoal mb-1 mt-1">Audio & Shareable Quotes</h3>
                <p className="text-sm text-charcoal-light mb-4">Listen to audio narration and create shareable quote images for social media.</p>
                <Link to="/chapter/1"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all">
                  <ExternalLink className="w-5 h-5" /> Go to Chapters
                </Link>
              </div>
            </div>
          </div>

          {/* Interactive Web Version */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-sky/20 flex items-center justify-center flex-shrink-0">
                <Headphones className="w-7 h-7 text-sky-dark" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-charcoal mb-1">Interactive Web Version</h3>
                <p className="text-sm text-charcoal-light mb-4">The full experience with audio, interactive journaling, progress tracking, and iCloud backup.</p>
                <Link to="/contents" data-testid="read-online-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all">
                  <ExternalLink className="w-5 h-5" /> Read Online Now
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-stone-200 text-center">
          <p className="text-sm text-charcoal-light">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-2">&copy; {new Date().getFullYear()} All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
